<template>
  <div class="description-wrapper">
    <v-textarea
      outlined
      spellcheck="false"
      :label="label"
      ref="main-text-area"
      v-model="text"
      :rows="rows"
      :disabled="disabled"
      :readonly="readonly"
      @keyup="checkText"
      @focus="onFocus"
      @blur="isOnFocus = true"
      @click="checkPosition"
      @paste="onFocus"
      @scroll="setScrollTop($event.target.scrollTop)"
    >
      <div slot="prepend-inner" v-if="settings.spellchecker">
        <div
          v-show="isOnFocus && !spellcheckApiFailed"
          class="highlight-text"
          ref="highlight-text"
          :class="{
            'highlight-text--hide': fetching || typing
          }"
          v-html="highlightText"
        ></div>

        <div v-show="spellcheckApiFailed" class="spellcheck-error-icon">
          <v-icon color="warning">mdi-alert</v-icon>
        </div>
      </div>
    </v-textarea>
    <v-tooltip v-if="isOnFocus && settings.spellchecker && !spellcheckApiFailed" bottom color="success darken-2">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-bind="attrs"
          v-on="on"
          class="spellcheck-info"
          fab
          x-small
          elevation="1"
          :color="countOfSpellErrors ? 'error' : 'success'"
          @click.prevent="correctAll"
        >
          <span v-if="countOfSpellErrors && !fetching">{{ countOfSpellErrors }}</span>
          <v-icon v-if="countOfSpellErrors === 0 && !fetching">mdi-check</v-icon>
          <v-icon v-if="fetching">mdi-loading mdi-spin</v-icon>
        </v-btn>
      </template>
      <span>{{countOfSpellErrors ? 'Correct all' : 'Everything OK'}}</span>
    </v-tooltip>
    <div class="d-flex align-center"
      :class="spellErrorType"
      v-if="replaceOn.length"
    >
      <span class="mr-4">{{spellErrorMessage}}</span>
      <v-select
        class="ma-0 replacements-select"
        label="Replacements"
        dense
        hide-details
        :items="replaceOn"
        item-text="value"
        item-value="value"
        @change="correctSpellingError"
      >
      </v-select>
    </div>
  </div>
</template>
<script>
import axios from 'axios'

export default {
  name: 'DescriptionWithSpellCheck',
  props: {
    value: {
      required: false,
      type: [String, Number],
      default: ''
    },
    label: {
      required: false,
      type: [String, Number]
    },
    readonly: {
      required: false,
      type: Boolean,
      default: false
    },
    rows: {
      required: false,
      type: Number,
      default: 4
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      text: '',
      highlightText: '',
      spellingErrors: [],
      timeoutForCheck: null,
      fetching: false,
      isOnFocus: false,
      typing: false,
      spellErrorMessage: '',
      spellErrorType: '',
      replaceOn: [],
      carretPos: 0,
      offset: 0,
      textAreaScrollTop: 0,
      lastTextLength: 0,
      spellcheckApiFailed: false,
      apiErrorDisplayed: false
    }
  },
  computed: {
    settings () {
      return this.$store.getters.getSettings
    },
    countOfSpellErrors () {
      return this.spellingErrors.length
    }
  },
  mounted () {
    this.text = this.value
  },
  methods: {
    onFocus (e) {
      setTimeout(() => {
        if (this.settings.spellchecker) {
          this.isOnFocus = true
          this.textAreaScrollTop = e.target.scrollTop
          if (this.spellingErrors.length) {
            this.applyHighlights()
          } else {
            if (this.lastTextLength !== this.text.length) {
              this.checkText(e)
              this.lastTextLength = this.text.length
            }
          }
        }
      })
    },
    checkText (e) {
      if (this.settings.spellchecker) {
        this.textAreaScrollTop = e.target.scrollTop
        if (!this.text.length) {
          this.spellingErrors = []
          this.highlightText = ''
          this.spellErrorMessage = ''
          this.replaceOn = []
          this.spellcheckApiFailed = false
        }
        if (e.keyCode <= 40 && e.keyCode >= 37) {
          this.checkPosition(e)
        } else {
          this.typing = true
        }
        if (!this.fetching) {
          clearTimeout(this.timeoutForCheck)
          this.timeoutForCheck = setTimeout(() => {
            this.getSpellingErrors(e)
          }, 1000)
        } else {
          setTimeout(() => {
            this.typing = false
          }, 1000)
        }
      }
      this.returnValue()
    },
    getSpellingErrors () {
      if (!this.text.trim().length) {
        this.fetching = false
        this.spellcheckApiFailed = false
        return
      }
      this.fetching = true
      const params = new URLSearchParams()
      params.append('text', this.text)
      params.append('language', 'en-US')
      axios.post(process.env.VUE_APP_GRAMMARBOT_ENDPOINT, params, {
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
          'x-rapidapi-key': process.env.VUE_APP_GRAMMARBOT_API_KEY,
          'x-rapidapi-host': process.env.VUE_APP_GRAMMARBOT_API_HOST
        }
      }).then(res => {
        this.spellcheckApiFailed = false
        this.apiErrorDisplayed = false
        this.typing = false
        this.spellingErrors = res.data.matches
        this.fetching = false
        this.applyHighlights()
      }).catch(() => {
        this.spellcheckApiFailed = true
        this.spellingErrors = []
        this.highlightText = ''
        this.fetching = false
        if (!this.apiErrorDisplayed) {
          this.$store.dispatch('setInfo', { infoMsg: 'Spell check unavailable' })
          this.apiErrorDisplayed = true
        }
      })
    },
    applyHighlights () {
      this.highlightText = ''
      let lastIndex = 0
      for (let i = 0; i < this.spellingErrors.length; i++) {
        const begin = this.spellingErrors[i].offset
        const end = begin + this.spellingErrors[i].length
        const ruleId = this.spellingErrors[i].rule.id
        const r = new RegExp(this.text.substring(begin, end), 'g')
        const str = this.text.substring(lastIndex, end).replace(r, `<mark class="${this.getSpellClass(ruleId)}">$&</mark>`)
        this.highlightText += str
        lastIndex = end
      }
      if (lastIndex < this.text.length) {
        this.highlightText += this.text.substring(lastIndex)
      }
      this.offset = this.text.length
      setTimeout(() => {
        this.setScrollTop(this.textAreaScrollTop)
      })
    },
    setScrollTop (e) {
      if (this.settings.spellchecker && this.$refs['highlight-text']) {
        this.$refs['highlight-text'].scrollTop = e
      }
    },
    checkPosition (e) {
      this.carretPos = e.target.selectionStart
      if (this.spellingErrors.length) {
        const eItem = this.spellingErrors.filter(item => {
          return item.offset <= this.carretPos && (item.offset + item.length) >= this.carretPos
        })[0]
        this.spellErrorMessage = eItem ? eItem.message : ''
        this.spellErrorType = eItem ? this.getSpellClass(eItem.rule.id) : ''
        this.replaceOn = eItem ? eItem.replacements : []
      }
    },
    correctSpellingError (val) {
      let eIndex = -1
      const eItem = this.spellingErrors.filter((item, index) => {
        if (item.offset <= this.carretPos && (item.offset + item.length) >= this.carretPos) {
          eIndex = index
          return true
        }
      })[0]
      if (eIndex > -1) {
        this.text = this.text.substring(0, eItem.offset) + val + this.text.substring(eItem.offset + eItem.length)
        this.removeError(eIndex)
        this.spellErrorMessage = ''
        this.replaceOn = []
      }
      this.returnValue()
    },
    removeError (i) {
      this.offset = this.text.length - this.offset
      for (let ind = 0; ind < this.spellingErrors.length; ind++) {
        if (ind > i) this.spellingErrors[ind].offset += this.offset
      }
      this.spellingErrors.splice(i, 1)
      this.applyHighlights()
      this.$refs['main-text-area'].focus()
    },
    getSpellClass (ruleId) {
      let res = 'spelling-error-1'
      switch (ruleId) {
        case 'MORFOLOGIK_RULE_EN_US':
          res = 'spelling-error-2'
          break
        case 'WHITESPACE_RULE':
          res = 'spelling-error-3'
          break
      }
      return res
    },
    returnValue () {
      this.$emit('input', this.text)
      this.$emit('change', this.text)
    },
    correctAll () {
      if (this.spellingErrors.length) {
        while (this.spellingErrors.length) {
          this.carretPos = this.spellingErrors[0].offset
          this.correctSpellingError(this.spellingErrors[0].replacements[0].value)
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .highlight-text {
    position: absolute;
    top: 8px;
    left: 16px;
    right: 0;
    height: 100%;
    color: transparent;
    font-family: 'Roboto', sans-serif;
    line-height: 1.75rem;
    font-size: 1rem;
    font-weight: 400;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow: auto;
    opacity: 1;
    transition: opacity .15s ease-in-out;
    &--hide {
      opacity: 0;
    }
    &::v-deep mark {
      display: inline-block;
      background-color: transparent;
      color: transparent;
      position: relative;
      border-radius: 3px;
      &:after {
        content: '';
        display: block;
        position: absolute;
        bottom: 2px;
        left: 0;
        background-color: rgba(255, 150, 0, .5);
        width: 100%;
        height: 2px;
        border-radius: 3px;
      }
      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 2px;
        left: -1px;
        right: -1px;
        bottom: 2px;
        border-radius: 3px;
        background-color: rgba(255, 150, 0, .5);
      }
      &.spelling-error-3 {
        &:before, &:after {
          background-color: rgba(255, 150, 0, .5);
        }
      }
      &.spelling-error-2 {
        &:before, &:after {
          background-color: rgba(0, 0, 255, .3);
        }
      }
      &.spelling-error-1 {
        &:before, &:after {
          background-color: rgba(255, 0, 0, .4);
        }
      }
    }
  }
  .spelling-error {
    &-1 {
      color: rgba(255, 0, 0, .75)
    }
    &-2 {
      color: rgba(0, 0, 255, .75);
    }
    &-3 {
      color: rgba(255, 150, 0, .9);
    }
  }
  .replacements-select {
    max-width: 250px;
  }
  .description-wrapper {
    position: relative;
  }
  .spellcheck-error-icon {
    position: absolute;
    z-index: 10;
    bottom: 5px;
    right: 5px;
  }
  .spellcheck-info {
    position: absolute;
    z-index: 10;
    bottom: 40px;
    right: 5px;
    color: rgba(0, 0, 0, .5);
    font-size: 12px;
    button {
      cursor: pointer;
    }
  }
  v-textarea {
    color: transparent;
  }
</style>
